import { ComponentType } from '@angular/cdk/portal';
import { Component, effect, inject, ViewChild, signal, AfterViewInit, OnDestroy } from '@angular/core';
import { FileUploadDropzoneComponent, FileUploadDropzoneModel } from '../../components/field-components/file-upload-dropzone/file-upload-dropzone.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PayerSelectComponent } from '../../components/field-components/payer-select/payer-select.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FileDataUploadStore } from '../../store/file-data-upload.store';
import { PayerItem } from '../../models/payer-item';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';
import { DropzoneTrackingComponent } from "../../components/field-components/file-upload-dropzone/dropzone-tracking/dropzone-tracking.component";
import { UtcDateTimeFormatter } from '../../helpers/utc-datetime-formatter';
import { LoadingDialogComponent } from '../loading-dialog/loading-dialog.component';
import { MimeTypeHelper } from '../../helpers/mime-type-helper';
import { FileTypeSelectComponent } from '../../components/field-components/file-type-select/file-type-select.component';
import { FileTypeFilterStore } from '../../store/file-type-filter.store';

export class FileUploadDialogModel {
  readonly component: ComponentType<FileUploadDialogComponent> = FileUploadDialogComponent;
  panelClass?: string | string[] = 'dialog-container';
  autoFocus?: boolean = false;
  maxWidth? = '95dvw';
  data;
  disableClose? = true;

  constructor(data) {
    this.data = data;
  }
}

@Component({
    selector: 'app-file-upload-dialog',
    imports: [
        FileUploadDropzoneComponent,
        PayerSelectComponent,
        MatButtonModule,
        MatTooltipModule,
        MatIcon,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        DropzoneTrackingComponent,
        FileTypeSelectComponent
    ],
    providers: [
        FileDataUploadStore,
        UtcDateTimeFormatter,
        MimeTypeHelper,
        FileTypeFilterStore,
    ],
    templateUrl: './file-upload-dialog.component.html',
    styleUrl: './file-upload-dialog.component.scss'
})
export class FileUploadDialogComponent implements AfterViewInit, OnDestroy {
  form: FormGroup;

  @ViewChild(FileUploadDropzoneComponent) morUploadField: FileUploadDropzoneComponent;
  @ViewChild(PayerSelectComponent) payerSelect: PayerSelectComponent;
  @ViewChild(FileTypeSelectComponent) fileTypeSelect: FileTypeSelectComponent;
  @ViewChild(DropzoneTrackingComponent) trackingComponent: DropzoneTrackingComponent;

  formBuilder = inject(FormBuilder);
  dropzoneModel: FileUploadDropzoneModel;
  payerTooltip = "Contains a listing of all payers that have a configured file mapping. If payer is not listed, contact ValueCycle Customer Service.";
  dropzoneTooltip = signal<string>("");
  private subscriptions = new Subscription();

  readonly store = inject(FileDataUploadStore);
  readonly data = inject(MAT_DIALOG_DATA);
  readonly filterStore = inject(FileTypeFilterStore);
  uploadError: string | null;

  constructor(
    public dialogRef: MatDialogRef<FileUploadDialogComponent>,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.form = this.formBuilder.group({});
    this.dropzoneModel = this.data.item;

    const validFileTypeString = this.data.item.validMimeTypes
          .map((type) => MimeTypeHelper.getFileExtension(type) || type) // Replace MIME types with extensions or fallback to MIME type
          .join(", ")
          .replace(/, ([^,]*)$/, ", and $1");
    
    this.dropzoneTooltip.set(`Accepted file types: ${validFileTypeString}\n\nTo upload a file, please ensure it is under the maximum allowed size of 200 MB`);
  }

  ngAfterViewInit(): void {
    effect(() => {
      this.morUploadField.disabled = this.store.currentFile() != null;
    });
    effect(() => {
      this.uploadError = this.morUploadField.invalidReason();
    });
    effect(() => {
      this.fileTypeSelect.disabled = !this.payerSelect.hasSelection();
    });
    effect(() => {
      this.store.setSelectedFileType(this.fileTypeSelect.valueChanged());
    });
  }
  handlePayerSelected(event: PayerItem) {
    if (event) {
      this.store.setSelectedPayer(event);
    }
  }
  handleFileDropped(event: File[]) {
    if (event) {
      this.trackingComponent.addFiles(event);
      this.store.setCurrentFile(event[0]); 
    }
    if (!this.store.selectedPayer()) {
      this.payerSelect.markAsTouched();
    }
  }
  handleFileDeleted(event: File[]) {
    if (event) {
      this.store.setCurrentFile(null);
    }
  }
  handleUploadSubmit() {
    const spinner = this.dialog.open(LoadingDialogComponent, 
      {
        disableClose: true,
        panelClass: 'custom-spinner-dialog'
      });
    this.store.uploadCurrentFile().then((result) => {
      if (result) {
        spinner.close();
        this.dialogRef.close(true);
      } else {
        spinner.close();
      }
    });
  }
  handleCancel() {
    if (this.store.currentFile()) {
      const message = 'Clicking Cancel will not save the file selected for upload. ' 
        + 'Do you want to continue to close and not save?';
      const title = 'Warning';
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "450px",
        data: new ConfirmDialogModel(title, message, '')
      });

      this.subscriptions.add(
        dialogRef.afterClosed().subscribe(result => {
          if(result) {
            this.store.resetState();
            this.dialogRef.close(false);
          }
        })
      );
    } else {
      this.dialogRef.close(false);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}